/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import { Route, Redirect, useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import LogRocket from 'logrocket';

import {
  EMAIL_SEARCH_PARAM,
  PROMO_ID_PARAM,
  DEEP_LINK_STORAGE_KEY,
  REDIRECT_SEARCH_PARAM,
  ROUTE_LOGIN,
  ROUTE_LOGOUT,
  TYPE_SEARCH_PARAM,
} from '../../../config/routes';

import { useFirebaseAuth } from '../../../../global/FirebaseProvider/FirebaseProvider';
import { setGa4UserId } from '../../../scripts/gaUserId';
import { getSearchParam, isRedeemPath, isSharePath } from '../../../utils/routes';
import { getUserName, getUUID } from '../../../utils/utils';
import { UTM_KEYS } from '../../../utils/url';
import safeStorage from '../../../utils/localStorage';

const PrivateRoute = ({
  component: Component,
  componentProps = {},
  ...rest
}) => {
  const { isSignedIn, isLoading, user, userData } = useFirebaseAuth();
  const { location } = useHistory();
  const posthog = usePostHog();

  useEffect(() => {
    if (!isSignedIn || isLoading) return;

    // Use same normalized affiliate_cid for GA4 and PostHog (matches backend normalizeAffiliateCid)
    const normalizedCid = getUUID(userData);
    setGa4UserId(normalizedCid);

    if (normalizedCid) {
      if (posthog.get_distinct_id() !== normalizedCid) {
        posthog.identify(normalizedCid, { email: userData?.email });
      }
      LogRocket.identify(String(normalizedCid), {
        email: userData?.email,
        name: getUserName(userData),
      });
    }
  }, [isSignedIn, isLoading, user, userData]);

  if (isLoading || (isSignedIn && !userData?.affiliate_cid)) {
    return null;
  }

  // Handle deep link
  if (!isSignedIn) {
    // Store the route the user tried to visit
    // Only handle if the current location is not the login page, register flow, or share page
    if (
      location.pathname !== ROUTE_LOGIN &&
      location.pathname !== ROUTE_LOGOUT &&
      !isRedeemPath(location.pathname) &&
      !isSharePath(location.pathname)
    ) {
      const search = new URLSearchParams(location.search);
      // Set login search params
      const loginSearch = new URLSearchParams({});
      const typeParam = getSearchParam(search, TYPE_SEARCH_PARAM);
      const emailParam = getSearchParam(search, EMAIL_SEARCH_PARAM);
      const promoParam = getSearchParam(search, PROMO_ID_PARAM);
      if (typeParam) {
        loginSearch.set(TYPE_SEARCH_PARAM, typeParam);
      }
      if (emailParam) {
        loginSearch.set(EMAIL_SEARCH_PARAM, emailParam);
      }
      if (promoParam) {
        loginSearch.set(PROMO_ID_PARAM, promoParam);
      }
      // Copy only UTM params to login URL (avoid leaking view-specific params)
      UTM_KEYS.forEach((key) => {
        const value = search.get(key);
        if (value) loginSearch.set(key, value);
      });
      // Persist deep-link destination so it survives the email-verification
      // round-trip. URL param covers same-tab reload; localStorage covers
      // the case where the verification email opens in a different tab.
      const deepLinkDest = location.pathname + location.search + location.hash;
      loginSearch.set(REDIRECT_SEARCH_PARAM, deepLinkDest);
      safeStorage.setItem(DEEP_LINK_STORAGE_KEY, deepLinkDest);
      // Remove login search params for redirect
      search.delete(TYPE_SEARCH_PARAM);
      search.delete(EMAIL_SEARCH_PARAM);
      search.delete(PROMO_ID_PARAM);

      return (
        <Redirect
          to={{
            pathname: ROUTE_LOGIN,
            search: loginSearch.toString(),
            state: {
              from: {
                ...location,
                search: search.toString(),
              },
            },
          }}
        />
      );
    }
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isSignedIn && !isLoading) {
          return <Component {...props} {...componentProps} />;
        }

        return <Redirect to={ROUTE_LOGIN} />;
      }}
    />
  );
};

export default PrivateRoute;
